import * as React from 'react';
import { Typography, InputAdornment, Tooltip } from '@mui/material';

import Select from 'components/Select';
import Input, { TextInput } from 'components/Input';
import { formatDollar, formatPercent } from 'helpers/formatters';
import {
    addTriaPremiumToRatedPremium,
    calculateAnnualizedPremiumFromRateAndLimit,
    calculatePremiumFromRateAndLimit,
    calculateTotalPremiumFromRateAndLimit,
} from 'helpers/calculations';
import { numberMaskWhole, negativeNumberMask, numberMask } from 'helpers/masks';
import { Location } from 'types/location.type';
import PropertyTableRowModel from './PropertyTableRowModel';
import { LocationOptionsData } from 'types/types.type';
import { YES_NO_OPTIONS } from '../../../../constants';
import WhhDeductibleBuilder from './WhhDeductibleBuilder';
import { SubmissionDetails } from '../../../../types/submission.type';

export interface PropertyTableBodyFieldProps {
    location: Location;
    locationOptions: LocationOptionsData;
    matchingRowData: PropertyTableRowModel;
    classes: any;
    readonly: boolean;
    isLocationRenewal: boolean;
    isLocationProrataEndorsement: boolean;
    handleChange: (value: string, id: string) => void;
    effectiveDate: string;
    expirationDate?: string;
}

export const getPropertyTableBodyFields = (props: PropertyTableBodyFieldProps) => {
    const {
        classes,
        handleChange,
        isLocationProrataEndorsement,
        isLocationRenewal,
        location,
        locationOptions,
        matchingRowData,
        readonly,
        effectiveDate,
        expirationDate,
    } = props;
    const locationClassType = location.locationClassType;
    const isFloodAllowed = ['C', 'D', 'X', 'X_UNSHADED'].includes(location.floodZoneType);
    if (!isFloodAllowed) {
        matchingRowData.FL = 'NO';
    }
    const isFlood = matchingRowData.FL?.toUpperCase() === 'YES';
    const isEq = matchingRowData.EQ?.toUpperCase() === 'YES';
    return [
        {
            value: location.locationName,
            style: { textAlign: 'left', color: '#000000', fontWeight: 600 },
        },
        {
            value: (
                <Typography style={{ marginBottom: 0 }}>
                    {locationOptions.LOCATION_CLASSES[locationClassType]}
                </Typography>
            ),
        },
        {
            value: readonly ? (
                <Typography style={{ marginBottom: 0 }}>{matchingRowData.AOP}</Typography>
            ) : (
                <Select
                    classes={{ select: classes.select }}
                    inputId={`${location.id}:AOP`}
                    options={YES_NO_OPTIONS}
                    defaultValue={YES_NO_OPTIONS.YES}
                    value={matchingRowData.AOP?.toUpperCase()}
                    onChange={(event: any) => handleChange(event.target.value, `${location.id}:AOP`)}
                />
            ),
        },
        {
            value: readonly ? (
                <Typography style={{ marginBottom: 0 }}>{matchingRowData.AOP_DED}</Typography>
            ) : (
                <Select
                    classes={{ select: classes.select }}
                    inputId={`${location.id}:AOP_DED`}
                    optionsList={Object.values(locationOptions.LOCATION_AOP_OPTIONS)}
                    defaultValue={'$100,000'}
                    value={matchingRowData.AOP_DED}
                    onChange={(event: any) => handleChange(event.target.value, `${location.id}:AOP_DED`)}
                />
            ),
        },
        {
            value: readonly ? (
                <Typography style={{ marginBottom: 0 }}>{matchingRowData.WD}</Typography>
            ) : (
                <Select
                    classes={{ select: classes.select }}
                    inputId={`${location.id}:WD`}
                    options={YES_NO_OPTIONS}
                    defaultValue={YES_NO_OPTIONS.YES}
                    value={matchingRowData.WD?.toUpperCase()}
                    onChange={(event: any) => handleChange(event.target.value, `${location.id}:WD`)}
                />
            ),
        },
        {
            value: readonly ? (
                <Typography style={{ marginBottom: 0 }}>{matchingRowData.WD_DED}</Typography>
            ) : (
                <Select
                    classes={{ select: classes.select }}
                    inputId={`${location.id}:WD_DED`}
                    optionsList={Object.values(locationOptions.LOCATION_AOP_OPTIONS)}
                    defaultValue={'$100,000'}
                    value={matchingRowData.WD_DED}
                    onChange={(event: any) => handleChange(event.target.value, `${location.id}:WD_DED`)}
                />
            ),
        },
        {
            value: readonly ? (
                <Typography style={{ marginBottom: 0 }}>{matchingRowData.WHH}</Typography>
            ) : (
                <Select
                    classes={{ select: classes.select }}
                    inputId={`${location.id}:WHH`}
                    options={YES_NO_OPTIONS}
                    defaultValue={YES_NO_OPTIONS.YES}
                    value={matchingRowData.WHH?.toUpperCase()}
                    onChange={(event: any) => handleChange(event.target.value, `${location.id}:WHH`)}
                />
            ),
        },
        {
            value: readonly ? (
                <Typography style={{ marginBottom: 0 }}>{matchingRowData.WHH_DED}</Typography>
            ) : (
                <TextInput
                    className={classes.dedBuilderInput}
                    inputProps={{ size: matchingRowData?.WHH_DED?.length || 15 }}
                    placeholder="$100,000"
                    value={matchingRowData.WHH_DED}
                    readOnly={true}
                    variant="underlined"
                    spacing={0}
                    onBlur={(event: any) => handleChange(event.target.value, `${location.id}:WHH_DED`)}
                    endAdornment={
                        <WhhDeductibleBuilder
                            onChange={(value: any) => handleChange(value, `${location.id}:WHH_DED`)}
                            currentDeductible={matchingRowData.WHH_DED}
                        />
                    }
                />
                // <Select
                //     classes={{ select: classes.select }}
                //     inputId={`${location.id}:WHH_DED`}
                //     optionsList={Object.values(locationOptions.LOCATION_WHH_OPTIONS)}
                //     defaultValue={'$100,000'}
                //     value={matchingRowData.WHH_DED}
                //     onChange={(event: any) => handleChange(event.target.value, `${location.id}:WHH_DED`)}
                // />
            ),
        },
        {
            value: readonly ? (
                <Typography style={{ marginBottom: 0 }}>{matchingRowData.NS}</Typography>
            ) : (
                <Select
                    classes={{ select: classes.select }}
                    inputId={`${location.id}:NS`}
                    options={YES_NO_OPTIONS}
                    defaultValue={YES_NO_OPTIONS.YES}
                    value={matchingRowData.NS?.toUpperCase()}
                    onChange={(event: any) => handleChange(event.target.value, `${location.id}:NS`)}
                />
            ),
        },
        {
            value: readonly ? (
                <Typography style={{ marginBottom: 0 }}>{matchingRowData.NS_DED}</Typography>
            ) : (
                <TextInput
                    className={classes.dedBuilderInput}
                    inputProps={{ size: matchingRowData?.NS_DED?.length || 15 }}
                    placeholder="3% of TIV"
                    value={matchingRowData.NS_DED}
                    readOnly={true}
                    variant="underlined"
                    spacing={0}
                    onBlur={(event: any) => handleChange(event.target.value, `${location.id}:NS_DED`)}
                    endAdornment={
                        <WhhDeductibleBuilder
                            onChange={(value: any) => handleChange(value, `${location.id}:NS_DED`)}
                            currentDeductible={matchingRowData.NS_DED}
                        />
                    }
                />
                // <Select
                //     classes={{ select: classes.select }}
                //     inputId={`${location.id}:NS_DED`}
                //     optionsList={Object.values(locationOptions.LOCATION_WHH_OPTIONS)}
                //     defaultValue={'3% of TIV'}
                //     value={matchingRowData.NS_DED}
                //     onChange={(event: any) => handleChange(event.target.value, `${location.id}:NS_DED`)}
                // />
            ),
        },
        {
            value: readonly ? (
                <Typography style={{ marginBottom: 0 }}>{matchingRowData.FL}</Typography>
            ) : (
                <Select
                    title={!isFloodAllowed ? `Cannot Offer Flood due to Flood Zone: ${location.floodZoneType}` : ''}
                    classes={{ select: classes.select }}
                    inputId={`${location.id}:FL`}
                    options={YES_NO_OPTIONS}
                    defaultValue={YES_NO_OPTIONS.YES}
                    disabled={!isFloodAllowed}
                    value={matchingRowData.FL?.toUpperCase()}
                    onChange={(event: any) => handleChange(event.target.value, `${location.id}:FL`)}
                />
            ),
        },
        {
            value: readonly ? (
                <Typography style={{ marginBottom: 0 }}>{matchingRowData.FL_LIMIT}</Typography>
            ) : (
                <Select
                    classes={{ select: classes.select }}
                    inputId={`${location.id}:FL_LIMIT`}
                    optionsList={Object.values(locationOptions.LOCATION_FLOOD_EQ_LIMIT_OPTIONS)}
                    disabled={!isFlood}
                    defaultValue={'$0'}
                    value={matchingRowData.FL_LIMIT}
                    onChange={(event: any) => handleChange(event.target.value, `${location.id}:FL_LIMIT`)}
                />
            ),
        },
        {
            value: readonly ? (
                <Typography style={{ marginBottom: 0 }}>{matchingRowData.FL_DED}</Typography>
            ) : (
                <Select
                    classes={{ select: classes.select }}
                    inputId={`${location.id}:FL_DED`}
                    optionsList={Object.values(locationOptions.LOCATION_FLOOD_EQ_OPTIONS)}
                    disabled={!isFlood}
                    defaultValue={'$100,000'}
                    value={matchingRowData.FL_DED}
                    onChange={(event: any) => handleChange(event.target.value, `${location.id}:FL_DED`)}
                />
            ),
        },
        {
            value: readonly ? (
                <Typography style={{ marginBottom: 0 }}>{matchingRowData.EQ}</Typography>
            ) : (
                <Select
                    classes={{ select: classes.select }}
                    inputId={`${location.id}:EQ`}
                    options={YES_NO_OPTIONS}
                    defaultValue={YES_NO_OPTIONS.YES}
                    value={matchingRowData.EQ?.toUpperCase()}
                    onChange={(event: any) => handleChange(event.target.value, `${location.id}:EQ`)}
                />
            ),
        },
        {
            value: readonly ? (
                <Typography style={{ marginBottom: 0 }}>{matchingRowData.EQ_LIMIT}</Typography>
            ) : (
                <Select
                    classes={{ select: classes.select }}
                    inputId={`${location.id}:EQ_LIMIT`}
                    optionsList={Object.values(locationOptions.LOCATION_FLOOD_EQ_LIMIT_OPTIONS)}
                    disabled={!isEq}
                    defaultValue={'$0'}
                    value={matchingRowData.EQ_LIMIT}
                    onChange={(event: any) => handleChange(event.target.value, `${location.id}:EQ_LIMIT`)}
                />
            ),
        },
        {
            value: readonly ? (
                <Typography style={{ marginBottom: 0 }}>{matchingRowData.EQ_DED}</Typography>
            ) : (
                <Select
                    classes={{ select: classes.select }}
                    inputId={`${location.id}:EQ_DED`}
                    optionsList={Object.values(locationOptions.LOCATION_FLOOD_EQ_OPTIONS)}
                    defaultValue={'$100,000'}
                    disabled={!isEq}
                    value={matchingRowData.EQ_DED}
                    onChange={(event: any) => handleChange(event.target.value, `${location.id}:EQ_DED`)}
                />
            ),
        },
        {
            value: <Typography style={{ marginBottom: 0 }}>{location.zone || '--'}</Typography>,
        },
        {
            value: (
                <Typography style={{ marginBottom: 0 }}>
                    {matchingRowData.expiringBaseRate ? formatDollar(matchingRowData.expiringBaseRate, 3) : 'N/A'}
                </Typography>
            ),
            isHidden: readonly || !isLocationRenewal,
        },
        {
            value: (
                <Typography style={{ marginBottom: 0 }}>
                    {matchingRowData.expiringCrdb ? formatPercent(matchingRowData.expiringCrdb / 100.0, 0) : 'N/A'}
                </Typography>
            ),
            isHidden: readonly || !isLocationRenewal,
        },
        {
            value: (
                <Typography style={{ marginBottom: 0 }}>
                    {matchingRowData.expiringFinalRate ? formatDollar(matchingRowData.expiringFinalRate, 3) : 'N/A'}
                </Typography>
            ),
            isHidden: readonly || !isLocationRenewal,
        },
        {
            value: (
                <Input
                    classes={{ input: classes.input }}
                    inputId={`${location.id}:baseRate`}
                    isFormattedNumber
                    maxDecimals={3}
                    placeholder={'$0.000'}
                    readonly={readonly}
                    onBlur={(event: any) => handleChange(event.target.value, `${location.id}:baseRate`)}
                    defaultValue={formatDollar(location.baseRate || 0, 3)}
                    value={formatDollar(matchingRowData.baseRate || 0, 3)}
                />
            ),
        },
        {
            style: { minWidth: '100px' },
            value: readonly ? (
                <Typography style={{ marginBottom: 0 }}>
                    {matchingRowData.CR_DB || matchingRowData.CR_DB === 0
                        ? matchingRowData.CR_DB
                        : (readonly && '') || ''}
                </Typography>
            ) : (
                <TextInput
                    inputStyle={{ textAlign: 'right' }}
                    placeholder="0.0"
                    value={
                        matchingRowData.CR_DB || matchingRowData.CR_DB === 0
                            ? matchingRowData.CR_DB
                            : (readonly && '') || ''
                    }
                    variant="underlined"
                    onBlur={(event: any) => handleChange(event.target.value, `${location.id}:CR_DB`)}
                    endAdornment={<InputAdornment position="end">%</InputAdornment>}
                    mask={negativeNumberMask}
                />
            ),
        },
        {
            value: (
                <Typography style={{ marginBottom: 0 }}>{formatDollar(matchingRowData.finalRate || 0, 3)}</Typography>
            ),
        },
        {
            value: (
                <Typography style={{ marginBottom: 0 }}>
                    {formatDollar(location.totalInsurableValue || 0, 0)}
                </Typography>
            ),
        },
        {
            value: (
                <Tooltip title="This value has been Rounded to nearest number">
                    <Typography style={{ marginBottom: 0 }}>
                        {formatDollar(
                            isLocationProrataEndorsement
                                ? calculateAnnualizedPremiumFromRateAndLimit(
                                      matchingRowData.finalRate,
                                      location.totalInsurableValue
                                  )
                                : calculatePremiumFromRateAndLimit(
                                      matchingRowData.finalRate,
                                      location.totalInsurableValue,
                                      effectiveDate,
                                      expirationDate
                                  ) || 0,
                            0
                        )}
                    </Typography>
                </Tooltip>
            ),
        },
        {
            style: { minWidth: '100px' },
            value: readonly ? (
                <Typography style={{ marginBottom: 0 }}>{matchingRowData.triaPercentage + '%'}</Typography>
            ) : (
                <TextInput
                    inputStyle={{ textAlign: 'right' }}
                    placeholder="2.0"
                    value={matchingRowData.triaPercentage}
                    variant="underlined"
                    onBlur={(event: any) => handleChange(event.target.value, `${location.id}:triaPercentage`)}
                    endAdornment={<InputAdornment position="end">%</InputAdornment>}
                    mask={numberMask}
                />
            ),
        },
        {
            value: (
                <Tooltip title="This value has been Rounded to nearest number">
                    <Typography style={{ marginBottom: 0 }}>
                        {formatDollar(
                            isLocationProrataEndorsement
                                ? addTriaPremiumToRatedPremium(
                                      calculateAnnualizedPremiumFromRateAndLimit(
                                          matchingRowData.finalRate,
                                          location.totalInsurableValue
                                      ),
                                      matchingRowData.triaPercentage
                                  )
                                : calculateTotalPremiumFromRateAndLimit(
                                      matchingRowData.finalRate,
                                      location.totalInsurableValue,
                                      matchingRowData.triaPercentage,
                                      effectiveDate,
                                      expirationDate
                                  ) || 0,
                            0
                        )}
                    </Typography>
                </Tooltip>
            ),
        },
        {
            value: (
                <Tooltip title="This value has been Rounded to nearest number">
                    <Typography style={{ marginBottom: 0 }}>
                        {formatDollar(
                            readonly ? matchingRowData.totalPremium || 0 : matchingRowData.endorsementPremium || 0
                        )}
                    </Typography>
                </Tooltip>
            ),
            isHidden: !isLocationProrataEndorsement,
        },
        {
            value: readonly ? (
                <Typography style={{ marginBottom: 0 }}>
                    {(matchingRowData.inspectionsRequiredCount || 0) > 0 ? YES_NO_OPTIONS.YES : YES_NO_OPTIONS.NO}
                </Typography>
            ) : (
                <Select
                    classes={{ select: classes.select }}
                    inputId={`${location.id}:inspectionsRequiredCount`}
                    options={YES_NO_OPTIONS}
                    defaultValue={YES_NO_OPTIONS.YES}
                    value={(matchingRowData.inspectionsRequiredCount || 0) > 0 ? YES_NO_OPTIONS.YES : YES_NO_OPTIONS.NO}
                    onChange={(event: any) =>
                        handleChange(
                            event.target.value.toString().toUpperCase() === YES_NO_OPTIONS.YES.toUpperCase()
                                ? '1'
                                : '0',
                            `${location.id}:inspectionsRequiredCount`
                        )
                    }
                />
            ),
        },
        {
            value: readonly ? (
                <Typography style={{ marginBottom: 0 }}>{matchingRowData.lumenRating || '--'}</Typography>
            ) : (
                <TextInput
                    inputStyle={{ textAlign: 'right' }}
                    placeholder="1-10"
                    value={matchingRowData.lumenRating || (readonly && '--') || ''}
                    variant="underlined"
                    mask={numberMaskWhole}
                    onChange={(event: any) => {
                        const { value } = event.target;
                        const numEntered = Number(value);
                        const adjustedVal = numEntered > 10 ? 10 : numEntered === 0 ? '' : value || '';
                        handleChange(adjustedVal, `${location.id}:lumenRating`);
                    }}
                />
            ),
        },
    ].filter((r) => !r.isHidden);
};
